import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = window.location.origin + '/api';
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_a85f478b-763e-4b7e-9f54-0dbb5a37a168/artifacts/fk4lgbat_Logo%20Phocea%20Rent.png";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!token) {
      setError('Lien de réinitialisation invalide');
    }
  }, [token]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!formData.password) {
      toast.error('Veuillez entrer un nouveau mot de passe');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.password
      });
      
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès !');
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.detail || 'Lien expiré ou invalide');
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#DFFF00]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#DFFF00]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_WHITE} 
            alt="Phocea Rent" 
            className="h-16 w-auto mx-auto mb-6 cursor-pointer"
            onClick={() => navigate('/')}
          />
          <h1 className="text-2xl font-bold text-white mb-2">
            Réinitialiser le mot de passe
          </h1>
          <p className="text-slate-400">
            Créez un nouveau mot de passe sécurisé
          </p>
        </div>

        {/* Reset Card */}
        <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
          
          {/* Error State */}
          {error && !success && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Lien invalide</h3>
              <p className="text-slate-400 mb-6">{error}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold"
              >
                Retour à la connexion
              </Button>
            </div>
          )}

          {/* Success State */}
          {success && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Mot de passe réinitialisé !</h3>
              <p className="text-slate-400 mb-6">
                Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold"
              >
                Se connecter
              </Button>
            </div>
          )}

          {/* Reset Form */}
          {!error && !success && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-slate-300">Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Min. 8 caractères"
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300">Confirmer le mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>

              {/* Password requirements */}
              <div className="bg-slate-800/50 rounded-lg p-3 mt-4">
                <p className="text-xs text-slate-400 mb-2">Le mot de passe doit contenir :</p>
                <ul className="text-xs space-y-1">
                  <li className={`flex items-center gap-2 ${formData.password.length >= 8 ? 'text-green-400' : 'text-slate-500'}`}>
                    {formData.password.length >= 8 ? <CheckCircle className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current" />}
                    Au moins 8 caractères
                  </li>
                  <li className={`flex items-center gap-2 ${formData.password === formData.confirmPassword && formData.password.length > 0 ? 'text-green-400' : 'text-slate-500'}`}>
                    {formData.password === formData.confirmPassword && formData.password.length > 0 ? <CheckCircle className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current" />}
                    Les mots de passe correspondent
                  </li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold py-6 rounded-xl mt-6"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Réinitialiser le mot de passe'
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Back to login */}
        {!success && (
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-slate-400 hover:text-white transition-colors text-sm"
            >
              ← Retour à la connexion
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
